import { createSlice } from "@reduxjs/toolkit";

const getInitialUser = () => {
  try {
    const cached = JSON.parse(localStorage.getItem("linko_user_profile") || "null");
    if (cached && typeof cached === "object") return cached;
  } catch (_e) {}
  return null;
};

const getInitialTokenStatus = () => {
  try {
    const cached = JSON.parse(localStorage.getItem("linko_token_status") || "null");
    if (cached) {
      const avail =
        typeof cached.availableTokens === "number"
          ? cached.availableTokens
          : typeof cached.available_tokens === "number"
          ? cached.available_tokens
          : null;
      const total =
        typeof cached.tokenTotal === "number"
          ? cached.tokenTotal
          : typeof cached.total_tokens === "number"
          ? cached.total_tokens
          : null;
      const used =
        total !== null && avail !== null ? Math.max(0, total - avail) : 0;
      return {
        tokenData: {
          available_tokens: avail,
          total_tokens: total,
          used_tokens: used,
        },
        tokenBalance: avail,
        tokenLimit: total,
      };
    }
  } catch (_e) {}
  return {
    tokenData: null,
    tokenBalance: null,
    tokenLimit: null,
  };
};

const getInitialActiveTranslation = () => {
  try {
    const cached = JSON.parse(localStorage.getItem("linko_active_translation") || "null");
    if (cached && typeof cached === "object") return cached;
  } catch (_e) {}
  return null;
};

const initialToken = getInitialTokenStatus();

const initialState = {
  user: getInitialUser(),
  tokenData: initialToken.tokenData,
  tokenBalance: initialToken.tokenBalance,
  tokenLimit: initialToken.tokenLimit,
  isTokenLoading: false,
  activeTranslation: getInitialActiveTranslation(),
};

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUser: (state, action) => {
      const payload = action?.payload;
      const userObj = payload?.data !== undefined ? payload.data : payload;
      state.user = userObj;
      try {
        if (userObj) {
          localStorage.setItem("linko_user_profile", JSON.stringify(userObj));
        } else {
          localStorage.removeItem("linko_user_profile");
        }
      } catch (_e) {}
    },
    updateUserProfile: (state, action) => {
      const payload = action?.payload;
      const userObj = payload?.data !== undefined ? payload.data : payload;
      if (userObj) {
        state.user = { ...(state.user || {}), ...userObj };
        try {
          localStorage.setItem("linko_user_profile", JSON.stringify(state.user));
        } catch (_e) {}
      }
    },
    clearUser: (state) => {
      state.user = null;
      try {
        localStorage.removeItem("linko_user_profile");
      } catch (_e) {}
    },
    setTokenStatus: (state, action) => {
      const payload = action?.payload;
      if (payload === undefined || payload === null) return;
      const raw = payload?.data !== undefined ? payload.data : payload;

      const available =
        raw?.available_tokens !== undefined
          ? Number(raw.available_tokens)
          : raw?.availableTokens !== undefined
          ? Number(raw.availableTokens)
          : raw?.tokenBalance !== undefined
          ? Number(raw.tokenBalance)
          : typeof raw === "number"
          ? raw
          : state.tokenBalance;

      const total =
        raw?.total_tokens !== undefined
          ? Number(raw.total_tokens)
          : raw?.totalTokens !== undefined
          ? Number(raw.totalTokens)
          : raw?.tokenTotal !== undefined
          ? Number(raw.tokenTotal)
          : raw?.tokenLimit !== undefined
          ? Number(raw.tokenLimit)
          : state.tokenLimit;

      const used =
        raw?.used_tokens !== undefined
          ? Number(raw.used_tokens)
          : raw?.usedTokens !== undefined
          ? Number(raw.usedTokens)
          : available !== null && total !== null
          ? Math.max(0, total - available)
          : 0;

      const formatted = {
        ...(typeof raw === "object" && raw !== null ? raw : {}),
        available_tokens: available,
        total_tokens: total,
        used_tokens: used,
      };

      state.tokenData = formatted;
      state.tokenBalance = available;
      state.tokenLimit = total;
      state.isTokenLoading = false;

      // Sync to localStorage for fallback / persistence
      try {
        if (available !== null || total !== null) {
          localStorage.setItem(
            "linko_token_status",
            JSON.stringify({
              availableTokens: available,
              tokenTotal: total,
              available_tokens: available,
              total_tokens: total,
              used_tokens: used,
            })
          );
        }
      } catch (_e) {}
    },
    setTokenBalance: (state, action) => {
      state.tokenBalance = action.payload;
      if (state.tokenData) {
        state.tokenData.available_tokens = action.payload;
        if (state.tokenLimit !== null && action.payload !== null) {
          state.tokenData.used_tokens = Math.max(0, state.tokenLimit - action.payload);
        }
      }
    },
    setTokenLimit: (state, action) => {
      state.tokenLimit = action.payload;
      if (state.tokenData) {
        state.tokenData.total_tokens = action.payload;
      }
    },
    setTokenLoading: (state, action) => {
      state.isTokenLoading = action.payload;
    },
    deductTokens: (state, action) => {
      const amount = Number(action.payload) || 0;
      if (amount > 0 && typeof state.tokenBalance === "number") {
        state.tokenBalance = Math.max(0, state.tokenBalance - amount);
        if (state.tokenData) {
          state.tokenData.available_tokens = state.tokenBalance;
          state.tokenData.used_tokens = (state.tokenData.used_tokens || 0) + amount;
        }
        try {
          const cached = JSON.parse(localStorage.getItem("linko_token_status") || "{}");
          localStorage.setItem(
            "linko_token_status",
            JSON.stringify({
              ...cached,
              availableTokens: state.tokenBalance,
              available_tokens: state.tokenBalance,
              used_tokens: state.tokenData?.used_tokens,
            })
          );
        } catch (_e) {}
      }
    },
    resetTokenStatus: (state) => {
      state.tokenData = null;
      state.tokenBalance = null;
      state.tokenLimit = null;
      state.isTokenLoading = false;
      try {
        localStorage.removeItem("linko_token_status");
      } catch (_e) {}
    },
    setActiveTranslation: (state, action) => {
      const payload = action?.payload;
      state.activeTranslation = payload;
      try {
        if (payload) {
          localStorage.setItem("linko_active_translation", JSON.stringify(payload));
        } else {
          localStorage.removeItem("linko_active_translation");
        }
      } catch (_e) {}
    },
    clearActiveTranslation: (state) => {
      state.activeTranslation = null;
      try {
        localStorage.removeItem("linko_active_translation");
      } catch (_e) {}
    },
  },
});

export const {
  setUser,
  updateUserProfile,
  clearUser,
  setTokenStatus,
  setTokenBalance,
  setTokenLimit,
  setTokenLoading,
  deductTokens,
  resetTokenStatus,
  setActiveTranslation,
  clearActiveTranslation,
} = userSlice.actions;

export default userSlice.reducer;
